import type { Element, ElementContent, Literal, Root, RootContent } from "hast";

type RawContent = Literal & { type: "raw" };

const allowedElements = new Set([
  "a",
  "abbr",
  "address",
  "area",
  "article",
  "aside",
  "audio",
  "b",
  "bdi",
  "bdo",
  "blockquote",
  "br",
  "button",
  "caption",
  "cite",
  "code",
  "col",
  "colgroup",
  "dd",
  "del",
  "details",
  "dfn",
  "div",
  "dl",
  "dt",
  "em",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hr",
  "i",
  "img",
  "input",
  "ins",
  "kbd",
  "label",
  "legend",
  "li",
  "main",
  "map",
  "mark",
  "nav",
  "ol",
  "optgroup",
  "option",
  "p",
  "picture",
  "pre",
  "q",
  "rp",
  "rt",
  "ruby",
  "s",
  "samp",
  "section",
  "select",
  "small",
  "source",
  "span",
  "strong",
  "style",
  "sub",
  "summary",
  "sup",
  "table",
  "tbody",
  "td",
  "textarea",
  "tfoot",
  "th",
  "thead",
  "time",
  "tr",
  "track",
  "u",
  "ul",
  "var",
  "video",
  "wbr",
  "svg",
  "circle",
  "clippath",
  "defs",
  "desc",
  "ellipse",
  "feblend",
  "fecolormatrix",
  "fecomponenttransfer",
  "fecomposite",
  "feconvolvematrix",
  "fediffuselighting",
  "fedisplacementmap",
  "fedistantlight",
  "fedropshadow",
  "feflood",
  "fefunca",
  "fefuncb",
  "fefuncg",
  "fefuncr",
  "fegaussianblur",
  "feimage",
  "femerge",
  "femergenode",
  "femorphology",
  "feoffset",
  "fepointlight",
  "fespecularlighting",
  "fespotlight",
  "fetile",
  "feturbulence",
  "filter",
  "foreignobject",
  "g",
  "image",
  "line",
  "lineargradient",
  "marker",
  "mask",
  "metadata",
  "path",
  "pattern",
  "polygon",
  "polyline",
  "radialgradient",
  "rect",
  "stop",
  "symbol",
  "text",
  "textpath",
  "title",
  "tspan",
  "use",
  "view",
  "directive-bad",
  "directive-good",
  "directive-faq",
  "directive-faqitem",
  "directive-challenge",
  "directive-point",
  "directive-details",
  "directive-sensei",
  "directive-doubt",
  "directive-answer",
  "directive-quizanswer",
  "directive-spirit",
  "directive-hare",
  "directive-tortoise",
  "directive-error",
  "directive-references",
  "directive-quote",
  "directive-tips",
  "directive-tips-lead",
  "directive-tips-compare",
  "directive-tips-good",
  "directive-tips-bad",
  "directive-tips-faq",
  "directive-tips-qa",
  "directive-runnable",
  "directive-chart",
  "directive-flowchart",
  "directive-roadmap",
  "directive-experience",
  "directive-figure",
  "directive-video",
  "directive-photo",
  "directive-tweet",
  "directive-miniquiz",
  "directive-chapterhead",
]);

const discardedElements = new Set([
  "script",
  "iframe",
  "object",
  "embed",
  "applet",
  "base",
  "link",
  "meta",
  "template",
  "noscript",
  "animate",
  "animatemotion",
  "animatetransform",
  "set",
  "discard",
]);

/** 本文の生の HTML として扱う要素か (描く要素と、消す要素)。どちらでもない名前は描けずに消える */
export function isKnownLessonHtmlElement(tagName: string): boolean {
  const name = tagName.toLowerCase();
  return allowedElements.has(name) || discardedElements.has(name);
}

const urlProperties = new Set([
  "action",
  "background",
  "cite",
  "data",
  "formaction",
  "href",
  "icon",
  "itemid",
  "longdesc",
  "manifest",
  "poster",
  "src",
  "usemap",
  "xlinkhref",
  "fallback",
]);

const cssProperties = new Set([
  "style",
  "fill",
  "stroke",
  "filter",
  "clippath",
  "mask",
  "markerstart",
  "markermid",
  "markerend",
  "cursor",
]);

const allowedProtocols = new Set([
  "http:",
  "https:",
  "mailto:",
  "tel:",
  "irc:",
  "ircs:",
  "xmpp:",
]);

function isSafeUrl(value: string): boolean {
  const normalized = value.trim().replace(/[\u0000-\u0020\u007f-\u009f]/g, "");
  try {
    return allowedProtocols.has(
      new URL(normalized, "https://lesson.invalid").protocol,
    );
  } catch {
    return false;
  }
}

function hasUnsafeCss(value: string): boolean {
  const normalized = value
    .replace(/\/\*[\s\S]*?\*\//g, "")
    .replace(
      /\\([0-9a-f]{1,6})(?:\r\n|[ \t\r\n\f])?/gi,
      (_match, hex: string) => {
        const point = Number.parseInt(hex, 16);
        return point > 0 && point <= 0x10ffff
          ? String.fromCodePoint(point)
          : "\ufffd";
      },
    )
    .replace(/\\(?:\r\n|[\r\n\f])/g, "")
    .replace(/\\(.)/g, "$1")
    .replace(/[\u0000-\u0020\u007f-\u009f]/g, "")
    .toLowerCase();
  return /javascript:|vbscript:|expression\(|-moz-binding:/.test(normalized);
}

/**
 * 座標が NaN の区間 (長さ 0 の線分を丸めるときの 0 割り) を、path の d から落とす。
 * ブラウザは NaN の所で描くのをやめ、コンソールに誤りを出す。次の区間の始点は直前の終点なので、残りはそのままつながる。
 * 始点 (M) が NaN なら使える形ではないので空にする。NaN が無い d は 1 字も変えない。
 * 区間は命令の字で切る。座標の組を暗黙に繰り返す区間 (L1 1 2 NaN) は、その区間ごと落とす
 */
export function repairPathData(d: string): string {
  if (!d.includes("NaN")) return d;
  const parts = d.replace(/NaN/g, "\u0000").split(/(?=[MmLlHhVvCcSsQqTtAaZz])/);
  if (parts[0].includes("\u0000")) return "";
  return parts.filter((part) => !part.includes("\u0000")).join("");
}

function sanitizeProperties(element: Element): void {
  for (const [property, value] of Object.entries(element.properties)) {
    const name = property.replace(/[-:]/g, "").toLowerCase();
    if (
      name.startsWith("on") ||
      [
        "srcdoc",
        "is",
        "innerhtml",
        "outerhtml",
        "dangerouslysetinnerhtml",
        "xmlbase",
      ].includes(name)
    ) {
      delete element.properties[property];
    } else if (
      urlProperties.has(name) &&
      typeof value === "string" &&
      !isSafeUrl(value)
    ) {
      delete element.properties[property];
    } else if (name === "srcset" || name === "ping") {
      // URL の列は一つでも危険な値があれば属性ごと除く。
      const urls = Array.isArray(value) ? value.join(" ") : String(value ?? "");
      const parts = name === "srcset" ? urls.split(",") : urls.split(/\s+/);
      if (parts.some((part) => !isSafeUrl(part.trim().split(/\s+/)[0] ?? ""))) {
        delete element.properties[property];
      }
    } else if (
      cssProperties.has(name) &&
      typeof value === "string" &&
      hasUnsafeCss(value)
    ) {
      delete element.properties[property];
    } else if (name === "d" && element.tagName === "path" && typeof value === "string") {
      element.properties[property] = repairPathData(value);
    } else if (
      element.tagName === "directive-video" &&
      name === "label" &&
      typeof value === "string" &&
      !isSafeUrl(value)
    ) {
      delete element.properties[property];
    }
  }
}

function sanitizeChildren(
  children: Array<RootContent | RawContent>,
): ElementContent[] {
  const safe: ElementContent[] = [];
  for (const node of children) {
    if (node.type === "text") {
      safe.push(node);
      continue;
    }
    if (node.type === "raw") {
      safe.push({ type: "text", value: node.value });
      continue;
    }
    if (node.type !== "element") continue;

    const name = node.tagName.toLowerCase();
    if (discardedElements.has(name)) continue;
    if (name === "style") {
      const css = node.children
        .map((child) => (child.type === "text" ? child.value : ""))
        .join("");
      // React emits style text verbatim, including tags decoded from SVG entities.
      if (css.includes("<") || hasUnsafeCss(css)) continue;
    }

    node.children = sanitizeChildren(node.children);
    if (!allowedElements.has(name)) {
      safe.push(...node.children);
      continue;
    }

    sanitizeProperties(node);
    safe.push(node);
  }
  return safe;
}

/** rehypeRaw の解析後に消毒し、SSR とブラウザで同じ許可範囲を使う。 */
export function rehypeSanitizeLessonHtml() {
  return (tree: Root): void => {
    tree.children = sanitizeChildren(tree.children);
  };
}
